import { Flex, Box, Text, Anchor, ActionIcon, Divider, Button, Grid } from "@mantine/core";
import {
  IconBrandLinkedin,
  IconBrandInstagram,
  IconBrandX,
  IconBrandPinterest,
  IconBrandFacebook,
  IconBrandReddit,
  IconBrandYoutube,
} from "@tabler/icons-react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import MobileHamburger from "./MobileHamburger";
import { useAuth } from "../../hooks/useAuth";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";

export default function LandingLayout() {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    trpc.maintenance.get
      .query()
      .then((res) => {
        const maintenanceEnabled = !!res?.webMaintenanceEnabled;

        if (maintenanceEnabled && location.pathname !== "/maintenance") {
          navigate("/maintenance", { replace: true });
        }

        if (!maintenanceEnabled && location.pathname === "/maintenance") {
          navigate("/home", { replace: true });
        }
      })
      .catch((err) => {
        handleTrpcError(err);
      });
  }, [location.pathname, navigate]);

  return (
    <Flex direction="column" style={{ minHeight: "100vh" }}>
      {/* Navbar */}
      <Box
        component="header"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          zIndex: 50,
          background: "#ffffff",
          borderBottom: "1px solid #e4e8ee",
          height: 81,
        }}
      >
        <Flex
          align="center"
          justify="space-between"
          style={{ height: 80, maxWidth: 1200, margin: "0 auto", padding: "0 20px", gap: 12 }}
        >
          {/* Logo */}
          <Anchor component={Link} to="/home" style={{ textDecoration: "none", display: "flex", alignItems: "center" }}>
            <img src="/gtLogo.svg" alt="GT Setu" style={{ height: 70, width: "auto", maxWidth: 150 }} />
          </Anchor>

          {/* Navigation */}
          <Flex gap={28} visibleFrom="sm" style={{ flex: 1, justifyContent: "center" }}>
            {[
              { href: "/", label: "Find Partner", external: false },
              { href: "https://gtsetu.com/features/", label: "Features", external: true },
              { href: "https://gtsetu.com/pricing/", label: "Pricing", external: true },
              { href: "https://gtsetu.com/support/", label: "Support", external: true },
            ].map(({ href, label, external }) =>
              external ? (
                <Anchor
                  key={href}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  c="#6b7a8d"
                  fw={600}
                  fz={14}
                  lh={1}
                  style={{ textDecoration: "none" }}
                >
                  {label}
                </Anchor>
              ) : (
                <Anchor
                  key={href}
                  component={Link}
                  to={href}
                  c="#6b7a8d"
                  fw={600}
                  fz={14}
                  lh={1}
                  style={{ textDecoration: "none" }}
                >
                  {label}
                </Anchor>
              )
            )}
          </Flex>

          {/* Auth */}
          <Flex gap={8} align="center">
            <Flex gap={8} align="center" visibleFrom="sm">
              <Button component={Link} to="/login" variant="default" fw={400} fz={14} c="#1e2a3a" px="sm" py="xs">
                Log In
              </Button>
              <Button component={Link} to="/signup" fw={400} fz={14} px="sm" py="xs" style={{ background: "#4571e6" }}>
                Sign Up
              </Button>
            </Flex>

            {/* Mobile hamburger */}
            <MobileHamburger isAuthenticated={isAuthenticated} />
          </Flex>
        </Flex>
      </Box>

      <Box
        component="main"
        style={{
          flex: 1,
          width: "100%",
          paddingTop: 81,
        }}
      >
        <Outlet />
      </Box>

      <Divider color="gray.3" />
      <Box component="footer" bg="white">
        <Box style={{ maxWidth: 1200, margin: "0 auto", padding: "48px 40px 16px" }}>
          {/* 4-column grid: brand | company | help | legal */}
          <Grid gutter={32} mb={68}>
            {/* Brand */}
            <Grid.Col span={{ base: 12, sm: 3 }}>
              <Box ta={{ base: "center", sm: "left" }}>
                <Anchor component={Link} to="/home" style={{ display: "inline-flex" }}>
                  <img src="/GTFullLogo.png" alt="GT Setu" style={{ height: 100, width: "auto" }} />
                </Anchor>
                <Text mt={12} fz={14} fw={400} lh="22.4px" c="#6b7a8d">
                  Trusted, verified global business network for secure collaboration.
                </Text>
              </Box>
            </Grid.Col>

            {/* Company */}
            <Grid.Col span={{ base: 4, sm: 3 }}>
              <Box ta="center">
                <Text fz={14} fw={700} tt="uppercase" lts="0.7px" c="#1e2a3a" mb={16} lh="22.4px">
                  Company
                </Text>
                <Flex direction="column" gap={8} align="center">
                  {[
                    { label: "Our Team", href: "https://gtsetu.com/our-team/" },
                    { label: "About Us", href: "https://gtsetu.com/about/" },
                    { label: "Brochure", href: "https://gtsetu.com/brochure/" },
                    { label: "Reviews", href: "https://gtsetu.com/reviews/" },
                  ].map(({ label, href }) => (
                    <Anchor key={href} href={href} target="_blank" rel="noopener noreferrer" fz={14} fw={500} c="#6b7a8d" lh="22.4px" style={{ textDecoration: "none" }}>
                      {label}
                    </Anchor>
                  ))}
                </Flex>
              </Box>
            </Grid.Col>

            {/* Help */}
            <Grid.Col span={{ base: 4, sm: 3 }}>
              <Box ta="center">
                <Text fz={14} fw={700} tt="uppercase" lts="0.7px" c="#1e2a3a" mb={16} lh="22.4px">
                  Help
                </Text>
                <Flex direction="column" gap={8} align="center">
                  {[
                    { label: "Support", href: "https://gtsetu.com/support/" },
                    { label: "Resources", href: "https://gtsetu.com/resources/" },
                    { label: "Blogs", href: "https://gtsetu.com/blogs/" },
                    { label: "News", href: "https://gtsetu.com/news/" },
                  ].map(({ label, href }) => (
                    <Anchor key={href} href={href} target="_blank" rel="noopener noreferrer" fz={14} fw={500} c="#6b7a8d" lh="22.4px" style={{ textDecoration: "none" }}>
                      {label}
                    </Anchor>
                  ))}
                </Flex>
              </Box>
            </Grid.Col>

            {/* Legal */}
            <Grid.Col span={{ base: 4, sm: 3 }}>
              <Box ta="center">
                <Text fz={14} fw={700} tt="uppercase" lts="0.7px" c="#1e2a3a" mb={16} lh="22.4px">
                  Legal
                </Text>
                <Flex direction="column" gap={8} align="center">
                  {[
                    { label: "Privacy Policy", href: "https://gtsetu.com/privacy/" },
                    { label: "Terms of Service", href: "https://gtsetu.com/terms/" },
                    { label: "Cookie Policy", href: "https://gtsetu.com/cookie-policy/" },
                    { label: "Compliance", href: "https://gtsetu.com/compliance/" },
                  ].map(({ label, href }) => (
                    <Anchor key={href} href={href} target="_blank" rel="noopener noreferrer" fz={14} fw={500} c="#6b7a8d" lh="22.4px" style={{ textDecoration: "none" }}>
                      {label}
                    </Anchor>
                  ))}
                </Flex>
              </Box>
            </Grid.Col>
          </Grid>

          <Box mb={24} />

          {/* Bottom bar */}
          <Flex justify="space-between" align="center" wrap="wrap" gap="md">
            <Text fz={14} c="#002E59">
              @ {new Date().getFullYear()} GTsetu. All rights reserved
            </Text>
            <Flex gap={8}>
              {[
                {
                  icon: <IconBrandLinkedin size={16} />,
                  bg: "#0077B5",
                  href: "https://www.linkedin.com/company/gt-setu/",
                },
                { icon: <IconBrandInstagram size={16} />, bg: "#E1306C", href: "https://www.instagram.com/gtsetu/" },
                { icon: <IconBrandX size={16} />, bg: "#000000", href: "https://x.com/gt_setu" },
                { icon: <IconBrandPinterest size={16} />, bg: "#E60023", href: "https://in.pinterest.com/gt_setu/" },
                { icon: <IconBrandFacebook size={16} />, bg: "#1877F2", href: "https://www.facebook.com/gtsetu/" },
                { icon: <IconBrandReddit size={16} />, bg: "#FF4500", href: "https://www.reddit.com/user/GT_Setu/" },
                { icon: <IconBrandYoutube size={16} />, bg: "#FF0000", href: "https://www.quora.com/profile/GT-Setu" },
              ].map(({ icon, bg, href }) => (
                <ActionIcon
                  key={href}
                  component="a"
                  href={href}
                  target="_blank"
                  radius="sm"
                  size={32}
                  style={{ background: bg, border: "none" }}
                >
                  {icon}
                </ActionIcon>
              ))}
            </Flex>
          </Flex>
        </Box>
      </Box>
    </Flex>
  );
}
